/* Settings · sign-up codes, school gate, term dates and setup progress.
   Design: ConsoleSettings.dc.html.

   Live: the sign-up code (getOrCreateFleetSignupCode / generateFleetSignupCode,
   the same fleet_signup_codes doc the classic dashboard writes) and term dates
   (the exact organizations update BusTracking's onSaveTermDates performs).
   Moving the gate pin and the other sections stay on the classic dashboard's
   Settings, which seals the location and owns those flows. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, getDocs, limit, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORT_WHATSAPP_HREF } from '../../utils/companyContact';
import { AppsPanel, DevicesPanel, TeamPanel } from './SettingsPanels';
import { generateFleetSignupCode, getOrCreateFleetSignupCode } from '../../fleet/generateFleetSignupCode';
import { CAMPUS_FENCE_RADIUS_M } from '../../components/fleet/SchoolLocationMap';
import { useFleet } from '../data/FleetData';
import { usePage, useShell } from '../Shell';
import { useTakeover } from '../zana/Takeover';
import MapView from '../MapView';
import { Empty, IcAlert, PageSkeleton, PageBody, StaleBanner, plural, useAction, useGo, useToast } from '../kit';

const SECT = ['School', 'Term dates', 'Sign-up codes', 'Apps', 'Team', 'Connected devices', 'Zana'] as const;
type Sect = typeof SECT[number];

/* Where the sections this page does not redo live on the classic dashboard. */

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isYmd = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
/** The org stores term dates as 'yyyy-mm-dd' strings; fall back to the parsed date. */
function storedYmd(raw: unknown, parsed: Date | null): string {
  if (typeof raw === 'string' && isYmd(raw.trim())) return raw.trim();
  return parsed ? ymd(parsed) : '';
}

/* ── sign-up code ─────────────────────────────────────────────────────── */
const CodePanel: React.FC<{ orgCode: string; orgName: string }> = ({ orgCode, orgName }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || '';
  const { toast } = useToast();
  const action = useAction();
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const asked = useRef(false);

  const load = useCallback(() => {
    if (!orgCode || !uid) return;
    setLoadErr(false);
    getOrCreateFleetSignupCode(orgCode, uid).then(setCode).catch(() => setLoadErr(true));
  }, [orgCode, uid]);
  // One read (and one write only if the last code has expired) when the panel opens.
  useEffect(() => { if (!asked.current && orgCode && uid) { asked.current = true; load(); } }, [orgCode, uid, load]);

  // Countdown only: a local clock, no reads or writes. Stops once the code expires.
  const left = code ? Math.max(0, code.expiresAt - tick) : 0;
  const expired = !!code && left <= 0;
  useEffect(() => {
    if (!code || expired) return;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [code, expired]);
  const mm = Math.floor(left / 60000);
  const ss = Math.floor((left % 60000) / 1000);

  const copy = () => {
    if (!code) return;
    const fail = () => toast({ kind: 'error', text: 'Couldn’t copy. Select the code and copy it by hand.' });
    if (!navigator.clipboard) { fail(); return; }
    navigator.clipboard.writeText(code.code).then(() => toast({ kind: 'success', text: `Code ${code.code} copied`, ms: 2500 }), fail);
  };
  const newCode = () => {
    if (!orgCode || !uid) return;
    const old = code && !expired ? code.code : null;
    void action({
      busy: 'Making a new code…',
      done: (r: { code: string }) => (old ? `New code ${r.code}. The old code ${old} stops working now.` : `New code ${r.code}. It works for 15 minutes.`),
      fail: 'Couldn’t make a new code. Try again in a minute.',
      run: async () => { const r = await generateFleetSignupCode(orgCode, uid); setCode(r); setTick(Date.now()); return r; },
    });
  };
  const waText = code
    ? `Join ${orgName} on Trasse: open the Trasse app, choose sign up and enter the code ${code.code}. It works for the next ${Math.max(1, Math.ceil(left / 60000))} minutes.`
    : '';

  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Sign-up codes</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>Drivers and parents type this code in the app to join {orgName}.</p>
        </div>
      </div>
      {loadErr && !code ? (
        <Empty icon={<IcAlert size={22} />} title="Couldn’t load the sign-up code" body="Check your connection, then try again.">
          <button type="button" className="btn s b-line" onClick={load}>Try again</button>
        </Empty>
      ) : (
        <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, opacity: expired ? 0.4 : 1 }} aria-label={code ? `Code ${code.code.split('').join(' ')}` : 'Loading code'} role="img">
            {(code ? code.code.split('') : ['', '', '', '']).map((d, i) => (
              <span key={i} className={'big' + (code ? '' : ' shim')} style={{ width: 56, height: 68, borderRadius: 10, border: '1px solid var(--line2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>{d}</span>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {code && (
              <span className={'chip ' + (expired ? 'c-bad' : 'c-teal')} style={{ alignSelf: 'flex-start' }} aria-live="off">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                </svg>
                {expired ? 'Expired · make a new code' : `Expires in ${mm}:${String(ss).padStart(2, '0')}`}
              </span>
            )}
            <span className="sm" style={{ color: 'var(--muted)' }}>One code works for 15 minutes and any number of people.</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn s b-ink" onClick={copy} disabled={!code || expired}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <rect x="8" y="8" width="13" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
                </svg>
                Copy
              </button>
              {code && !expired
                ? <a className="btn s b-line" href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noopener noreferrer">Share on WhatsApp</a>
                : <button type="button" className="btn s b-line" disabled>Share on WhatsApp</button>}
              <button type="button" className={'btn s ' + (expired ? 'b-ink' : 'b-ghost')} onClick={newCode} disabled={!uid}>New code</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ── term dates ───────────────────────────────────────────────────────── */
const TermPanel: React.FC<{ orgCode: string; start: string; end: string }> = ({ orgCode, start, end }) => {
  const action = useAction();
  const [s, setS] = useState(start);
  const [e, setE] = useState(end);
  const [touched, setTouched] = useState<{ s: boolean; e: boolean }>({ s: false, e: false });
  const [saving, setSaving] = useState(false);
  // Follow the live doc until the user starts editing.
  useEffect(() => { if (!touched.s) setS(start); }, [start, touched.s]);
  useEffect(() => { if (!touched.e) setE(end); }, [end, touched.e]);

  const sErr = touched.s && !isYmd(s) ? 'Enter the date the term starts.' : null;
  const eErr = touched.e && e !== '' && (!isYmd(e) ? 'Enter a full date, or leave it empty.' : isYmd(s) && e <= s ? 'The term must end after it starts.' : null);
  const valid = isYmd(s) && (e === '' || (isYmd(e) && e > s));
  const dirty = s !== start || e !== end;

  const save = () => {
    setTouched({ s: true, e: true });
    if (!valid) return;
    setSaving(true);
    void action({
      busy: 'Saving term dates…',
      done: 'Term dates saved',
      fail: 'Couldn’t save the term dates. Try again in a minute.',
      // Same write as the classic dashboard's onSaveTermDates (BusTracking).
      run: async () => {
        if (!orgCode) throw new Error('No organization.');
        const q = query(collection(db, 'organizations'), where('code', '==', orgCode), limit(1));
        const snap = await getDocs(q);
        if (!snap.docs[0]) throw new Error('Organization not found.');
        await updateDoc(doc(db, 'organizations', snap.docs[0].id), {
          currentTermStartDate: s || null,
          currentTermEndDate: e || null,
          updatedAt: serverTimestamp(),
        });
        setTouched({ s: false, e: false });
      },
    }).finally(() => setSaving(false));
  };

  const inputStyle: React.CSSProperties = { height: 44, borderRadius: 8, border: '1px solid var(--line2)', padding: '0 12px', font: 'inherit', fontWeight: 400, background: 'var(--s1)', color: 'var(--ink)' };
  const errLine = (msg: string, id: string) => (
    <span className="ferr-msg" id={id}><IcAlert size={15} />{msg}</span>
  );

  return (
    <section className="panel" style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, fontSize: 14 }}>
        Term starts
        <input id="term-start" type="date" value={s} onChange={(ev) => setS(ev.target.value)} onBlur={() => setTouched((t) => ({ ...t, s: true }))}
          className={sErr ? 'ferr' : undefined} aria-invalid={!!sErr} aria-describedby={sErr ? 'term-start-err' : undefined} style={inputStyle} />
        {sErr && errLine(sErr, 'term-start-err')}
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, fontSize: 14 }}>
        Term ends
        <input id="term-end" type="date" value={e} min={s || undefined} onChange={(ev) => setE(ev.target.value)} onBlur={() => setTouched((t) => ({ ...t, e: true }))}
          className={eErr ? 'ferr' : undefined} aria-invalid={!!eErr} aria-describedby={eErr ? 'term-end-err' : undefined} style={inputStyle} />
        {eErr && errLine(eErr, 'term-end-err')}
      </label>
      <p className="sm" style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--muted)' }}>
        Fuel and route charts follow these dates. Billing follows your plan.
      </p>
      <div className="row" style={{ gridColumn: '1 / -1', gap: 8 }}>
        <button type="button" className="btn s b-ink" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save term dates'}</button>
        {dirty && !saving && <button type="button" className="btn s b-ghost" onClick={() => { setS(start); setE(end); setTouched({ s: false, e: false }); }}>Undo changes</button>}
      </div>
    </section>
  );
};

/** The takeover tour plays once. This is the only place that brings it back. */
const ZanaPrefs: React.FC = () => {
  const take = useTakeover();
  const { toast } = useToast();
  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Zana</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>Five chats stay open in this browser. A chat is saved the moment you type or she answers. The takeover tour plays the first time she takes a job, and not again unless you ask here.</p>
        </div>
      </div>
      <div style={{ padding: 20 }}>
        <button type="button" className="btn b-line" onClick={() => {
          const ok = !!take?.replayTour();
          toast({ kind: 'info', text: ok ? 'The tour is open in Zana’s panel.' : 'Nothing for Zana to take over right now, so there is no tour to play.' });
        }}>Show the takeover tour</button>
      </div>
    </section>
  );
};

/* ── page ─────────────────────────────────────────────────────────────── */
const Settings: React.FC = () => {
  const f = useFleet();
  const { dark, openAddBus } = useShell();
  const go = useGo();
  const [cur, setCur] = useState<Sect>('Sign-up codes');

  usePage({
    key: 'ConsoleSettings', title: 'Settings',
    zanaContext: `Settings · ${cur.toLowerCase()}`,
    zanaIntro: 'I can explain any setting.',
    prompts: ['How do parents join?', 'How does the school gate work?', 'Why do term dates matter?'],
  });

  const orgName = f.org.name || 'your school';
  const termStart = storedYmd(f.org.raw.currentTermStartDate, f.org.termStart);
  const termEnd = storedYmd(f.org.raw.currentTermEndDate, f.org.termEnd);

  /* Setup, computed from the live records. */
  const steps: { label: string; done: boolean; fix?: React.ReactNode }[] = [
    // Only Trasse moves the school pin (2026-10-26): this opens the School
    // section, which shows it and says how to ask.
    { label: 'School location and gate', done: !!f.org.gate, fix: <button type="button" className="btn s b-line" onClick={() => setCur('School')}>See it</button> },
    { label: 'Buses and routes', done: f.buses.length > 0, fix: <button type="button" className="btn s b-line" onClick={openAddBus}>Add a bus</button> },
    { label: 'Drivers', done: f.drivers.length > 0, fix: <button type="button" className="btn s b-line" onClick={() => setCur('Sign-up codes')}>Get a code</button> },
    { label: 'Parents and children', done: f.parents.length > 0 && f.childrenTotal > 0, fix: <a className="btn s b-line" {...go('ConsolePeople', '?tab=parents')}>Open people</a> },
    { label: 'Term dates', done: !!termStart, fix: <button type="button" className="btn s b-line" onClick={() => setCur('Term dates')}>Add dates</button> },
  ];
  const doneN = steps.filter((x) => x.done).length;
  const pct = Math.round((doneN / steps.length) * 100);
  const missing = steps.filter((x) => !x.done).map((x) => x.label.toLowerCase());

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: 1040 }}>
        {f.error && <StaleBanner what="settings" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 className="h1">Settings</h1>
            <p className="sub">{orgName} · changes apply to every driver and parent the next time they open the app.</p>
          </div>
        </div>
        <div className="two" style={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
          <nav className="panel" style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }} aria-label="Settings sections">
            {SECT.map((t) => (
              <button key={t} type="button" className={`nav ${t === cur ? 'on' : ''}`} aria-current={t === cur ? 'page' : undefined} onClick={() => setCur(t)} style={{ width: '100%', border: 0, cursor: 'pointer', textAlign: 'left' }}>{t}</button>
            ))}
          </nav>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {cur === 'Sign-up codes' && <CodePanel orgCode={f.org.code} orgName={orgName} />}

            {cur === 'School' && (
              <section className="panel" style={{ overflow: 'hidden' }}>
                <div className="ph">
                  <div>
                    <h2 className="h2">School gate</h2>
                    <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>A bus inside this circle counts as arrived at school.</p>
                  </div>
                </div>
                {f.org.gate ? (
                  <>
                    <div style={{ height: 280 }}>
                      <MapView buses={[]} gate={f.org.gate} dark={dark} interactive={false} label={`Map of the ${orgName} gate`} />
                    </div>
                    <div style={{ padding: '14px 20px', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }} className="row">
                      <span className="mono">{f.org.gate.lat.toFixed(4).replace('-', '−')}, {f.org.gate.lng.toFixed(4).replace('-', '−')} · {CAMPUS_FENCE_RADIUS_M} m fence</span>
                      <span className="sm" style={{ color: 'var(--muted)' }}>To move it, contact Trasse.</span>
                    </div>
                  </>
                ) : (
                  /* The gate is set when the account is created; after that
                     only Trasse moves it (Gabriel, 2026-10-26). */
                  <Empty title="No school gate yet" body="Until the gate is set, buses cannot be marked as arrived at school. Trasse sets it for you.">
                    <a className="btn s b-ink" href={`${SUPPORT_WHATSAPP_HREF}?text=${encodeURIComponent(`Please set the school gate for ${orgName} (${f.org.code}).`)}`} target="_blank" rel="noreferrer">Ask Trasse on WhatsApp</a>
                  </Empty>
                )}
              </section>
            )}

            {cur === 'Term dates' && <TermPanel orgCode={f.org.code} start={termStart} end={termEnd} />}

            {/* These three opened the classic dashboard until 2026-10-28. */}
            {cur === 'Apps' && <AppsPanel f={f} />}
            {cur === 'Team' && <TeamPanel f={f} />}
            {cur === 'Connected devices' && <DevicesPanel f={f} />}
            {cur === 'Zana' && <ZanaPrefs />}

            <section className="panel">
              <div className="ph">
                <div>
                  <h2 className="h2">Setup</h2>
                  <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>
                    {doneN} of {steps.length} done.{missing.length ? ` Still to do: ${missing.join(', ')}.` : ' Everything the console needs is in place.'}
                  </p>
                </div>
                <span className={'chip ' + (pct === 100 ? 'c-ok' : 'c-teal')}>{pct}%</span>
              </div>
              {steps.map((x) => (
                <div key={x.label} className="todo" style={{ padding: '12px 20px' }}>
                  <span className="av" style={{ width: 26, height: 26, background: x.done ? 'var(--ok-tint)' : 'var(--s2)', color: x.done ? 'var(--ok)' : 'var(--muted)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                      <path d={x.done ? 'm5 12 5 5 9-10' : 'M12 5v14M5 12h14'} />
                    </svg>
                  </span>
                  <span style={{ flex: 1 }}>
                    {x.label}
                    <span className="vh">{x.done ? ' · done' : ' · not done yet'}</span>
                  </span>
                  {x.label === 'Buses and routes' && x.done && <span className="sm" style={{ color: 'var(--muted)' }}>{plural(f.buses.length, 'bus', 'buses')}</span>}
                  {x.label === 'Drivers' && x.done && <span className="sm" style={{ color: 'var(--muted)' }}>{plural(f.drivers.length, 'driver')}</span>}
                  {x.label === 'Parents and children' && x.done && <span className="sm" style={{ color: 'var(--muted)' }}>{plural(f.parents.length, 'parent')} · {plural(f.childrenTotal, 'child', 'children')}</span>}
                  {!x.done && x.fix}
                </div>
              ))}
            </section>
          </div>
        </div>
      </div>
    </PageBody>
  );
};

export default Settings;
